import { useEffect, useState, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Form } from "formik";

import { Container } from "./styles";

import Button from "../../components/Button";
import ButtonBack from "../../components/ButtonBack";
import Input from "../../components/Form/Input";
import ErrorMessage from "../../components/Form/ErrorMessage";
import Spacing from "../../components/Spacing";
import Text from "../../components/Text";
import registerSchemaCpf, {
  clearEmailsValidated,
  clearCpfsValidated,
} from "../../utils/schemas/registerCpf";
import registerSchemaCnpj from "../../utils/schemas/registerCnpj";
import { createUser, login, logout } from "../../services/user";
import m from "../../utils/messages";
import ErrorBox from "../../components/ErrorBox";
import { FormGroup, Label } from "../../components/Form";
import Loading from "../../components/Loading";
import PasswordRules from "../../components/PasswordRules";
import SimpleModal from "./modal";
import Swal from "sweetalert2";

const INITIAL_FORM_VALUES = {
  email: "",
  name: "",
  document: "",
  birthdate: "",
  password: "",
  password_confirmation: "",
  telephone: "",
  gender: "",
  gender_detail: "",
  dataUsageAgreement: false,
};

const removeNumbers = (value) => {
  return value?.replace(/[0-9]/g, '') || "";
};

// Função para formatar o documento (CPF ou CNPJ)
const formatDocument = (value) => {
  if (!value) return "";

  // Remove todos os caracteres não-numéricos
  const digits = value.replace(/\D/g, '');

  // Formata como CNPJ
  if (digits.length > 11) {
    return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .substring(0, 18); // 18 caracteres é o máximo para CNPJ formatado
  }

  // Formata como CPF
  return digits
    .replace(/(\d{3})(?=\d)/, '$1.')
    .replace(/(\d{3})(?=\d)/, '$1.')
    .replace(/(\d{3})(?=\d)/, '$1-')
    .substring(0, 14); // 14 caracteres é o máximo para CPF formatado
};

// Função para formatar números (remover caracteres não numéricos)
const formatNumbers = (value) => {
  if (!value) return "";
  
  // Remove todos os caracteres não-numéricos
  return value.replace(/\D/g, '');
};

// Função para validar CPF
const validateCPF = (cpf) => {
  cpf = cpf.replace(/[^\d]+/g, '');
  
  if (cpf.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cpf)) return false;
  
  // Valida os dígitos verificadores
  let sum = 0;
  let remainder;
  
  for (let i = 1; i <= 9; i++) {
    sum = sum + parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }
  
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(9, 10))) return false;
  
  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum = sum + parseInt(cpf.substring(i - 1, i)) * (12 - i);
  }
  
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(10, 11))) return false;
  
  return true;
};

// Função para validar CNPJ
const validateCNPJ = (cnpj) => {
  cnpj = cnpj.replace(/[^\d]+/g, '');
  
  if (cnpj.length !== 14) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cnpj)) return false;
  
  // Valida os dígitos verificadores
  let size = cnpj.length - 2;
  let numbers = cnpj.substring(0, size);
  const digits = cnpj.substring(size);
  let sum = 0;
  let pos = size - 7;
  
  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;
  
  size = size + 1;
  numbers = cnpj.substring(0, size);
  sum = 0;
  pos = size - 7;
  
  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }
  
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;
  
  return true;
};

const Register = () => {
  const history = useHistory();
  const rawLocationState = history.location?.state;
  const locationState = useMemo(() => rawLocationState || {}, [rawLocationState]);
  const ref = useRef(null);
  const [cpf, setCpf] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [dataUsageAgreementChecked, setDataUsageAgreementChecked] = useState(false);
  const [documentDigitsCount, setDocumentDigitsCount] = useState(0);
  const [expectedDigits, setExpectedDigits] = useState(11); // Começa com CPF
  const [documentAllowsAdditionalFields, setDocumentAllowsAdditionalFields] = useState(false);
  const [additionalFieldsRequested, setAdditionalFieldsRequested] = useState(false);
  const [documentType, setDocumentType] = useState("CPF");
  const [documentIsValid, setDocumentIsValid] = useState(false);
  const [invalidFieldsList, setInvalidFieldsList] = useState([]);
  const [documentError, setDocumentError] = useState("");
  const submitActionRef = useRef("finish");
  const [activeSubmitAction, setActiveSubmitAction] = useState("finish");

  const resumeStep = locationState?.resumeStep;
  const locationDataUsageAgreement = locationState?.dataUsageAgreement;
  const documentFromStateDigits = locationState.document?.replace(/\D/g, "");
  const identifierLabelFromState = locationState.email
    ? "e-mail"
    : documentFromStateDigits
      ? documentFromStateDigits.length > 11
        ? "CNPJ"
        : "CPF"
      : null;
  const emailPrefilledFromState = Boolean(locationState.email);
  const documentPrefilledFromState = Boolean(locationState.document);
  const initialFormValues = useMemo(() => {
    const { resumeStep: _ignoredResumeStep, ...stateValues } = locationState;
    const values = { ...INITIAL_FORM_VALUES, ...stateValues };

    if (locationState?.surname) {
      values.name = [locationState?.name, locationState?.surname]
        .filter(Boolean)
        .join(" ")
        .trim();
    }

    delete values.surname;

    return values;
  }, [locationState]);

  const [error, setError] = useState(null);

  const [passwordCriteria, setPasswordCriteria] = useState({
    uppercase: false,
    lowercase: false,
    number: false,
    length: false,
  });

  useEffect(() => {
    if (resumeStep === 2) {
      setAdditionalFieldsRequested(true);
      setDocumentAllowsAdditionalFields(true);
      setCurrentStep(2);
    }
  }, [resumeStep]);

  useEffect(() => {
    if (typeof locationDataUsageAgreement === "boolean") {
      setDataUsageAgreementChecked(locationDataUsageAgreement);
    }
  }, [locationDataUsageAgreement]);

  const handleCheckboxChange = () => {
    setDataUsageAgreementChecked(!dataUsageAgreementChecked);
  };

  const handleSuccess = (email, password) => {
    Swal.fire({
      title: "Cadastro realizado com sucesso",
      text: "Você será redirecionado de volta ao site",
      icon: "success",
      confirmButtonColor: "#CB3D42",
      didClose: async () => {
        try {
          const credentials = {
            email,
            password,
          };

          const data = await login(credentials);

          window.location.href = data.redirectTo;
        } catch (err) {
          setError(m("internalError"));
        }
      },
    });
  };

  const handleError = (error) => {
    console.log('Erro recebido:', error);

    let errorMessage = "Algo deu errado, tente novamente!";
    let errorTitle = "Atenção";
  
    // Verifica diferentes formatos possíveis de erro
    if (error?.response?.data?.errors) {
      // Verificar erro de CPF/CNPJ
      if (error.response.data.errors.document) {
        const docErrors = error.response.data.errors.document;
        
        if (docErrors.some(msg => msg.includes("já está sendo utilizado") || msg.includes("já está em uso"))) {
          errorTitle = `${documentType} já cadastrado`;
          errorMessage = `Este ${documentType} já está cadastrado em nossa base de dados. Por favor, tente fazer login ou recuperar sua senha.`;
        } else if (docErrors.some(msg => msg.includes("inválido"))) {
          errorTitle = `${documentType} inválido`;
          errorMessage = `O ${documentType} informado não é válido. Por favor, verifique os dados e tente novamente.`;
        } else {
          errorMessage = docErrors.join('<br>');
        }
      } else {
        errorMessage = Object.values(error.response.data.errors)
          .flat()
          .join('<br>');
      }
    } else if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.message) {
      errorMessage = error.message;
    }

    Swal.fire({
      icon: "error",
      title: errorTitle,
      html: errorMessage,
      confirmButtonColor: "#CB3D42",
      confirmButtonText: "OK"
    });
  };

  const handleDocumentChange = (e) => {
    const inputValue = e.target.value;

    // Remove tudo que não é número
    const digits = inputValue.replace(/\D/g, '');

    // Atualiza a contagem APENAS dos dígitos numéricos
    setDocumentDigitsCount(digits.length);
    
    // Define se é CPF ou CNPJ baseado no número de dígitos
    if (digits.length > 11) {
      setCpf(false);
      setExpectedDigits(14);
      setDocumentType("CNPJ");
    } else {
      setCpf(true);
      setExpectedDigits(11);
      setDocumentType("CPF");
    }

    // Formata e define o valor
    const formattedValue = formatDocument(digits);
    ref.current.setFieldValue('document', formattedValue);
    
    // Validar o documento e atualizar o estado de validade
    const isValidDocument = digits.length === 11 ? 
      validateCPF(digits) : 
      (digits.length === 14 ? validateCNPJ(digits) : false);
    
    setDocumentIsValid(isValidDocument);
    
    if (digits.length === 0) {
      setDocumentError("");
      setDocumentAllowsAdditionalFields(false);
    } else if (digits.length !== 11 && digits.length !== 14) {
      setDocumentError(`${documentType} incompleto. Por favor, insira todos os números.`);
      setDocumentAllowsAdditionalFields(false);
    } else if (!isValidDocument) {
      setDocumentError(`${documentType} inválido. Por favor, verifique os dados informados.`);
      setDocumentAllowsAdditionalFields(false);
    } else {
      setDocumentError("");
      setDocumentAllowsAdditionalFields(true);
    }
  };

  console.log('Mensagem debug empresa:', cpf ? "CPF" : "CNPJ");

// Correção final da função handleSubmit para pessoa física

  const resetSubmitAction = () => {
    submitActionRef.current = "finish";
    setActiveSubmitAction("finish");
  };

  const markSubmitAction = (action) => {
    submitActionRef.current = action;
    setActiveSubmitAction(action);
    setTimeout(() => {
      if (!ref.current || ref.current?.isSubmitting) {
        return;
      }
      resetSubmitAction();
    }, 0);
  };

  const handleSubmit = async (formData, { setSubmitting }) => {
    setFormSubmitAttempted(true);
    setSubmitting(true);
    const currentSubmitAction = submitActionRef.current || "finish";
    try {
      await logout();
    } catch (e) { }
    
    if (!dataUsageAgreementChecked) {
        Swal.fire({
          icon: "error",
          title: "Atenção",
          text: "É necessário aceitar os Termos de Uso.",
          confirmButtonColor: "#CB3D42",
        });
        setSubmitting(false);
        return;
      }

      const documentClean = formData.document
        ? formData.document
          .replaceAll(".", "")
          .replaceAll("-", "")
          .replaceAll("/", "")
      : "";
    
    // Verificar se o documento tem o número correto de dígitos
    const isCpf = documentClean.length <= 11;
    if ((isCpf && documentClean.length !== 11) || 
        (!isCpf && documentClean.length !== 14)) {
      Swal.fire({
        icon: "error",
        title: "Atenção",
        text: `${isCpf ? "CPF" : "CNPJ"} incompleto. Por favor, insira todos os dígitos.`,
        confirmButtonColor: "#CB3D42",
      });
      setSubmitting(false);
      return;
    }
    
    // Validar o documento
    const isValidDocument = isCpf ? 
      validateCPF(documentClean) : 
      validateCNPJ(documentClean);
      
    if (!isValidDocument) {
      Swal.fire({
        icon: "error",
        title: "Documento inválido",
        text: `O ${isCpf ? "CPF" : "CNPJ"} informado não é válido. Por favor, verifique os dados e tente novamente.`,
        confirmButtonColor: "#CB3D42",
      });
      setSubmitting(false);
      return;
    }

    const whoIs = isCpf ? "birthdate" : "date_foundation";
    
    // Verifica se data existe e tem formato correto
    if (!formData[whoIs] || formData[whoIs].length < 10) {
      Swal.fire({
        icon: "error",
        title: "Atenção",
        text: `${isCpf ? "Data de nascimento" : "Data de fundação"} inválida`,
        confirmButtonColor: "#CB3D42",
      });
      setSubmitting(false);
      return;
    }
    
    const day = formData[whoIs].slice(0, 2);
    const month = formData[whoIs].slice(3, 5);
    const year = formData[whoIs].slice(6, 10);

    const birth = `${year}-${month}-${day}`;

    // Dados base comuns para ambos os tipos
    const requestData = {
      is_company: !isCpf,
      email: formData.email,
      document: documentClean,
      password: formData.password,
      phone: (formData.telephone || '').replace(/\D/g, ''),
      data_usage_agreement: dataUsageAgreementChecked
    };

    // Separar primeiro nome e sobrenome do campo name (usado para CPF)
      const parts = (formData.name || '').trim();
      const [firstName, ...rest] = parts.split(' ');
      const surname = rest.join(" ");

    // Configurar os campos person e company corretamente:
    if (isCpf) {
      // Para CPF (pessoa física) - person deve ser um objeto direto, não um array
      requestData.person = {
        name: firstName || '',
        surname: surname || '',
        gender: formData.gender || '',
        gender_detail: formData.gender === 3 ? formData.gender_detail : null,
        date_birth: birth,
      };
      // Importante: não incluir company para CPF
    } else {
      // Para CNPJ (pessoa jurídica)
      const cleanedDocumentState = formData.document_state
        ? formData.document_state.replace(/\D/g, '')
        : '';

      requestData.company = {
        name: formData.name || '',
        fantasy_name: formData.fantasy_name || '',
        date_foundation: birth,
        document_state: cleanedDocumentState,
        state_registration: cleanedDocumentState,
      };
      // Importante: não incluir person para CNPJ
    }

    try {
      if (currentSubmitAction === "address") {
        const formSnapshot = JSON.parse(JSON.stringify(formData));
        const pendingRegistration = {
          userPayload: requestData,
          credentials: {
            email: requestData.email,
            password: requestData.password,
          },
          formValues: formSnapshot,
        };

        sessionStorage.setItem(
          '@pendingUserRegistration@',
          JSON.stringify(pendingRegistration)
        );

        history.push("/register/address", pendingRegistration);
        setSubmitting(false);
        resetSubmitAction();
        return;
      }

      console.log('Tipo de pessoa:', isCpf ? 'Física (CPF)' : 'Jurídica (CNPJ)');
      console.log('Estrutura do person:', isCpf ? 'objeto direto' : 'não incluído');
      console.log('Estrutura do company:', !isCpf ? 'objeto' : 'não incluído');
      const responseData = await createUser(requestData);
      clearEmailsValidated();
      clearCpfsValidated();
      const payload = responseData?.data;
      if (payload) {
        sessionStorage.removeItem('@pendingUserRegistration@');
        handleSuccess(requestData.email, requestData.password);
      } else {
        handleError(responseData);
      }
    } catch (apiErr) {
      console.error('API error:', apiErr);
      handleError(apiErr);
      setError(m("internalError"));
    }
    setSubmitting(false);
    resetSubmitAction();
  };
  // Corrigir problema de formatação ao redirecionar da tela de login
  useEffect(() => {
    // Se temos um documento no locationState
    if (locationState.document) {
      // Limpa a formatação primeiro para ter apenas os dígitos
      const digits = locationState.document.replace(/\D/g, '');

      // Determina se é CPF ou CNPJ
      const isCpf = digits.length <= 11;
      setCpf(isCpf);
      setExpectedDigits(isCpf ? 11 : 14);
      setDocumentType(isCpf ? "CPF" : "CNPJ");

      // Aplica a formatação adequada e atualiza o campo
      const formattedValue = formatDocument(digits);

      // Atualiza o campo do formulário com o valor formatado
      if (ref.current) {
        setTimeout(() => {
          ref.current.setFieldValue('document', formattedValue);

          // Validar o documento sem mostrar erros ainda
          const isValidDocument = isCpf ?
            (digits.length === 11 && validateCPF(digits)) :
            (digits.length === 14 && validateCNPJ(digits));

          setDocumentIsValid(isValidDocument);
          setDocumentDigitsCount(digits.length);

          if (isValidDocument) {
            setDocumentAllowsAdditionalFields(true);
            setDocumentError("");
          } else {
            // Não mostrar campos adicionais para documento inválido
            // mas também não mostrar erro inicialmente
            setDocumentAllowsAdditionalFields(false);
            setDocumentError("");
          }
        }, 100);
      }
    } else {
      // Comportamento padrão se não houver documento
      setCpf(true);
      setExpectedDigits(11);
      setDocumentType("CPF");
      setDocumentAllowsAdditionalFields(false);
      setAdditionalFieldsRequested(false);
    }
  }, [locationState.document]);

  const handlePasswordChange = (e) => {
    const value = ref?.current?.values?.password || '';

    setPasswordCriteria({
      uppercase: /[A-Z]/.test(value),
      lowercase: /[a-z]/.test(value),
      number: /[0-9]/.test(value),
      length: value.length >= 8,
    });
  };
  
  // Verificar campos inválidos para exibir mensagem de ajuda
  // Só exibir mensagens de erro após a primeira tentativa de submissão
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);
  
  useEffect(() => {
    if (formSubmitAttempted && ref.current && ref.current.errors) {
      const errors = ref.current.errors;
      const invalidFields = Object.keys(errors);
      setInvalidFieldsList(invalidFields);
    } else if (!formSubmitAttempted) {
      setInvalidFieldsList([]);
    }
  }, [ref.current?.errors, formSubmitAttempted]);

  useEffect(() => {
    if (formSubmitAttempted && ref.current && ref.current.errors) {
      const errors = ref.current.errors;
      const invalidFields = Object.keys(errors);
      setInvalidFieldsList(invalidFields);
    } else if (!formSubmitAttempted) {
      setInvalidFieldsList([]);
    }
  }, [ref.current?.errors, formSubmitAttempted]);

  const [currentStep, setCurrentStep] = useState(1);

  const handleBackNavigation = () => {
    if (additionalFieldsRequested) {
      setAdditionalFieldsRequested(false);
      setCurrentStep(1);
      return;
    }

    history.goBack();
  };

  return (
    <>
      <ButtonBack onClick={handleBackNavigation} title='Cadastro' totalSteps={3} step={currentStep} />
      <Container>
        <ErrorBox error={error} />

        <Formik
          initialValues={initialFormValues}
          validationSchema={cpf ? registerSchemaCpf : registerSchemaCnpj}
          validateOnMount
          validateOnChange={true}
          onSubmit={handleSubmit}
          innerRef={ref}
        >
          {({ isSubmitting, isValid, isValidating, values, setFieldValue, setFieldTouched, errors, touched }) => {
            const shouldShowAdditionalFields = additionalFieldsRequested && documentAllowsAdditionalFields;
            const unlockButtonDisabled =
              !values.email?.trim() ||
              !values.document?.trim() ||
              !documentAllowsAdditionalFields;

            return (
              <Form className="form login__form">
                {isValidating && <Loading text="Validando" />}
                {!shouldShowAdditionalFields && (
                  <>
                    <FormGroup>
                      {identifierLabelFromState && (
                        <Text
                          variant="regular"
                          style={{ color: "#CB3D42", marginBottom: 20 }}
                        >
                          Você digitou um {identifierLabelFromState} que não existe, entre novamente ou realize o cadastro abaixo.
                        </Text>
                      )}
                      <Input
                        name="email"
                        type="text"
                        label="Informe seu E-mail"
                        autoFocus={!emailPrefilledFromState}
                        maxLength={100}
                        disabled={emailPrefilledFromState}
                      />
                      <ErrorMessage name="email" />
                    </FormGroup>
                    <Spacing size={20} />
                    <FormGroup>
                      <Input
                        name="document"
                        type="text"
                        label="Informe seu CPF ou CNPJ*"
                        onChange={handleDocumentChange}
                        value={values.document}
                        disabled={documentPrefilledFromState}
                      />
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: "4px"
                      }}>
                        <small style={{
                          fontSize: "12px",
                          color: documentDigitsCount === expectedDigits ? "#28a745" : "#6c757d"
                        }}>
                          Tipo: {documentType} {documentIsValid && "✓"}
                        </small>
                        <small style={{
                          fontSize: "12px",
                          color: documentDigitsCount === expectedDigits ? "#28a745" : "#6c757d"
                        }}>
                          {documentDigitsCount}/{expectedDigits} dígitos
                        </small>
                      </div>
                      {documentError && (
                        <small style={{
                          fontSize: "12px",
                          color: "#dc3545",
                          display: "block",
                          marginTop: "4px"
                        }}>
                          {documentError}
                        </small>
                      )}
                      <ErrorMessage name="document" />
                    </FormGroup>
                    <Spacing size={20} />
                    <Button
                      variant="buttonPrimary"
                      text="Quero me cadastrar"
                      type="button"
                      onClick={() => {
                        setAdditionalFieldsRequested(true)
                        setCurrentStep(2)
                      }}
                      disabled={unlockButtonDisabled}
                      style={{ width: "100%" }}
                    />
                  </>
                )}
                {shouldShowAdditionalFields && (
                  <>
                    {cpf ? (
                      <>
                        <Text
                          variant="regular"
                          style={{
                            fontSize: "16px",
                            fontWeight: 500,
                            marginBottom: "12px"
                          }}
                        >
                          Complete seu cadastro e aproveite o melhor do{" "}
                          <span style={{ color: "#CB3D42", fontWeight: 600 }}>Zona Sul</span>:
                        </Text>
                        <Spacing size={20} />
                        <FormGroup>
                          <Input
                            name="name"
                            type="text"
                            label="Nome e sobrenome"
                            maxLength={150}
                            onChange={(event) => {
                              const sanitizedValue = removeNumbers(event.target.value)
                                .replace(/\s+/g, " ")
                                .replace(/^\s+/, "");
                              setFieldValue('name', sanitizedValue);
                            }}
                            value={values.name}
                          />
                          <ErrorMessage name="name" />
                        </FormGroup>
                      </>
                    ) : (
                        <>
                          <FormGroup>
                            <Input
                              name="name"
                              type="text"
                              label="Razão Social"
                              maxLength={150}
                            />
                            <ErrorMessage name="name" />
                          </FormGroup>
                          <Spacing size={20} />
                          <FormGroup>
                            <Input
                              name="fantasy_name"
                              type="text"
                              label="Nome Fantasia"
                              maxLength={100}
                            />
                            <ErrorMessage name="fantasy_name" />
                          </FormGroup>
                        </>
                      )}
                    <Spacing size={20} />
                    {cpf ? (
                      <FormGroup>
                        <Input
                          name="birthdate"
                          type="text"
                          mask="DD/DD/DDDD"
                          label="Data nascimento"
                        />
                        <ErrorMessage name="birthdate" />
                      </FormGroup>
                    ) : (
                        <FormGroup>
                          <Input
                            name="date_foundation"
                            type="text"
                            mask="DD/DD/DDDD"
                            label="Data de Fundação"
                          />
                          <ErrorMessage name="date_foundation" />
                        </FormGroup>
                      )}
                    <Spacing size={20} />
                    <FormGroup>
                      <Input
                        name="telephone"
                        type="text"
                        mask={["(DD)DDDD-DDDD", "(DD)DDDDD-DDDD"]}
                        label={cpf ? "Número de celular" : "Telefone"}
                        maxLength={15}
                      />
                      <ErrorMessage name="telephone" />
                    </FormGroup>
                    <Spacing size={20} />
                    {cpf ? (
                      <FormGroup>
                        <Label htmlFor="gender-select">Gênero</Label>
                        <select
                          id="gender-select"
                          name="gender"
                          value={
                            values.gender === null ||
                              values.gender === undefined ||
                              values.gender === ""
                              ? ""
                              : `${values.gender}`
                          }
                          onChange={(event) => {
                            const { value } = event.target;
                            const normalizedValue = value === "" ? "" : Number(value);
                            setFieldValue("gender", normalizedValue);
                            if (normalizedValue !== 3) {
                              setFieldValue("gender_detail", "");
                            }
                          }}
                          onBlur={() => setFieldTouched("gender", true)}
                          style={{
                            width: "100%",
                            padding: "10px",
                            borderRadius: "3px",
                            border: "1px solid #ccc",
                            backgroundColor: "#fff",
                            color: "#161414",
                            fontSize: "14px",
                            appearance: "none",
                          }}
                        >
                          <option value="">Selecione</option>
                          <option value={2}>Feminino</option>
                          <option value={1}>Masculino</option>
                          <option value={3}>Outros</option>
                          <option value={4}>Prefiro não responder</option>
                        </select>
                        <ErrorMessage name="gender" />
                        {Number(values.gender) === 3 && (
                          <div style={{ marginTop: "10px" }}>
                            <Input
                              name="gender_detail"
                              type="text"
                              label="Gênero (opcional)"
                              maxLength={15}
                              formater="lettersAndSpace"
                            />
                            <ErrorMessage name="gender_detail" />
                          </div>
                        )}
                      </FormGroup>
                    ) : (
                        <FormGroup>
                          <Input
                            name="document_state"
                            type="text"
                            label="Inscrição estadual"
                            value={values.document_state}
                            onChange={(e) => {
                              const formattedValue = formatNumbers(e.target.value);
                              setFieldValue('document_state', formattedValue);
                            }}
                          />
                        </FormGroup>
                      )}
                    <Spacing size={20} />
                    <FormGroup>
                      <Input
                        name="password"
                        type="password"
                        label="Crie uma senha"
                        onKeyUp={handlePasswordChange}
                        maxLength={50}
                      />
                      <ErrorMessage name="password" />
                    </FormGroup>
                    <Spacing size={20} />
                    <FormGroup>
                      <Input
                        name="password_confirmation"
                        type="password"
                        label="Confirme a senha"
                        maxLength={50}
                      />
                      <ErrorMessage name="password_confirmation" />
                    </FormGroup>
                    <Spacing size={20} />
                    <PasswordRules rules={passwordCriteria} />
                    <FormGroup>
                      <Label>
                        <input
                          type="checkbox"
                          name="dataUsageAgreement"
                          required
                          style={{ marginRight: "5px", marginBottom: "10px" }}
                          checked={dataUsageAgreementChecked}
                          onChange={handleCheckboxChange}
                        />
                      Declaro que concordo e estou ciente das utilizações dos meus{" "}
                        <span
                          style={{
                            color: "#7f7e7e",
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                          onClick={() => setModalOpen(true)}
                        >
                          Dados Pessoais
                      </span>
                      </Label>
                      <ErrorMessage name="dataUsageAgreement" />
                    </FormGroup>

                    
                    {modalOpen && (
                      <SimpleModal onClose={() => setModalOpen(false)}>
                        <p style={{ padding: "15px" }}>
                          As informações são livremente informadas pelo usuário, para
                          garantir a sua própria segurança, autorizando expressamente
                          o Zona Sul, a realizar todas as diligências que este julgue
                          necessárias para verificar a exatidão e a veracidade das
                          informações fornecidas, restando sujeito às pertinentes
                          penalidades legais caso sua veracidade não seja, de qualquer
                          modo, confirmada.
                        <br></br>
                          <br></br>Nós do Zona Sul valorizamos a privacidade e
                        proteção dos seus dados pessoais. Seus dados coletados neste
                        cadastro serão utilizados para única e exclusiva finalidade
                        de realizar o cadastro que habilita o uso da plataforma e
                        seus serviços.
                        <br></br>
                          <br></br>
                        Seus dados serão compartilhados com nossa parceira VTEX,
                        responsável pela base de dados, e não compartilharemos com
                        nenhum outro operador ou controlador, salvaguarda, para o
                        cumprimento de obrigações dispostas por lei, pelo poder
                        judiciário e/ou por qualquer outra autoridade competente.
                        <br></br>
                          <br></br>As informações permanecerão armazenadas pelo
                        período de 05 anos após a inatividade do seu cadastro,
                        seguido do devido descarte.
                      </p>
                      </SimpleModal>
                    )}

                    <Button
                      variant="buttonSecondary"
                      text="Cadastrar endereço"
                      type="submit"
                      onClick={() => markSubmitAction("address")}
                      disabled={!isValid || isSubmitting}
                      loading={isSubmitting && activeSubmitAction === "address"}
                      style={{ marginTop: "25px", width: "100%" }}
                    />

                    <Button
                      variant="buttonPrimary"
                      text="Cadastrar"
                      type="submit"
                      onClick={() => markSubmitAction("finish")}
                      disabled={!isValid || isSubmitting}
                      loading={isSubmitting && activeSubmitAction === "finish"}
                      style={{ marginTop: "15px", width: "100%" }}
                    />

                    {!isValid && formSubmitAttempted && invalidFieldsList.length > 0 && (
                      <div style={{
                        marginTop: "10px",
                        padding: "10px",
                        backgroundColor: "#f8d7da",
                        borderRadius: "4px",
                        color: "#721c24",
                        fontSize: "14px"
                      }}>
                        <p><strong>Por favor, corrija os seguintes campos para continuar:</strong></p>
                        <ul style={{ marginLeft: "20px", marginTop: "5px" }}>
                          {invalidFieldsList.map((field) => (
                            <li key={field}>
                              {field === "email" && "E-mail"}
                              {field === "document" && documentType}
                              {field === "name" && (cpf ? "Nome e sobrenome" : "Razão Social")}
                              {field === "fantasy_name" && "Nome Fantasia"}
                              {field === "birthdate" && "Data nascimento"}
                              {field === "date_foundation" && "Data de Fundação"}
                              {field === "telephone" && (cpf ? "Número de celular" : "Telefone")}
                              {field === "gender" && "Gênero"}
                              {field === "password" && "Senha"}
                              {field === "password_confirmation" && "Confirmação de Senha"}
                              {field === "dataUsageAgreement" && "Aceite dos Termos de Uso"}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </Form>
            );
          }}
        </Formik>
        <Text variant="support" style={{ textAlign: 'justify' }}>
          Ao se cadastrar, você concorda com nossa{" "}
          <a
            target="_blank"
            href="https://www.zonasul.com.br/politica-de-privacidade"
            rel="noopener noreferrer"
          >
            <span className="semibold">Política de Privacidade</span>
          </a>{" "}
          ou, se preferir, entre em contato com nosso{" "}
          <a
            target="_blank"
            href="https://www.zonasul.com.br/ajuda/fale-com-o-dpo"
            rel="noopener noreferrer"
          >
            <span className="semibold">Encarregado de Dados</span>
          </a>{" "}
          pelo canal oficial disponibilizado em nosso site.
        </Text>
      </Container>
    </>
  );
};

export default Register;
